"use client";

import { useEffect } from "react";

/**
 * Revalidate subscription state whenever the app becomes visible again.
 *
 * Covers the case where the user leaves to the Stripe portal from an active
 * state (menu "Manage subscription") and cancels — the subscription banner
 * component isn't mounted yet, so we can't rely on its own listener. Skips
 * during the post-checkout optimistic window (`subscriptionJustCompleted`) to
 * avoid clobbering the pending-webhook state with a Stripe refetch.
 *
 * Shared by both brand home wizards; the guard is subtle enough that a
 * copy-pasted duplicate would drift.
 */
export function useRevalidateSubscriptionOnVisible(
  mutateSubscription: () => void,
  subscriptionJustCompleted: boolean
) {
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (subscriptionJustCompleted) return;
      mutateSubscription();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [mutateSubscription, subscriptionJustCompleted]);
}
